/**
 * KYC (Know Your Customer) Service Module
 *
 * Manages user identity verification and KYC submissions.
 */

import { api } from './client';
import type { KycSubmitRequest, KycStatusResponse } from './types';

/**
 * KYC API
 *
 * Endpoints for identity verification and KYC management.
 */
export const kycAPI = {
  /**
   * Get KYC status
   *
   * Retrieves current KYC verification status.
   *
   * **Endpoint:** GET /api/kyc/status
   * **Protected:** Yes
   * **Method:** GET
   *
   * @returns KYC status
   * @throws ApiError on failure
   *
   * @example
   * ```typescript
   * const status = await kycAPI.getStatus();
   * console.log(status.status); // 'pending', 'approved', 'rejected'
   * ```
   */
  getStatus: async (): Promise<KycStatusResponse> => api.get<KycStatusResponse>('/api/kyc/status'),

  /**
   * Submit KYC information
   *
   * Submits identity verification documents for KYC review.
   *
   * **Endpoint:** POST /api/kyc/submit
   * **Protected:** Yes
   * **Method:** POST
   *
   * @param request - KYC submission data
   * @returns Submission confirmation
   * @throws ApiError on failure
   *
   * @example
   * ```typescript
   * const result = await kycAPI.submit({
   *   full_name: 'John Doe',
   *   id_number: '123456789',
   *   id_type: 'passport',
   *   document_url: 'https://...'
   * });
   * ```
   */
  submit: async (request: KycSubmitRequest): Promise<KycStatusResponse> =>
    api.post<KycStatusResponse>('/api/kyc/submit', request),
};
